// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Stack, Box, Typography } from '@metarouter-private/mr-mui';
import IntegrationAvatarGroup from 'app/shared/components/integrations/IntegrationAvatarGroup';
import TimeAgoAndDate from 'app/components/TimeAgoAndDate';
import SearchInput from './SearchInput';

export default function ConfirmFileList({ files, description }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) {
      return files;
    }
    const query = searchQuery.toLowerCase();
    return files.filter(file => file.pipeline?.name?.toLowerCase().includes(query));
  }, [files, searchQuery]);

  return (
    <Stack spacing={2}>
      {description && <Typography>{description}</Typography>}
      <SearchInput value={searchQuery} onChange={setSearchQuery} />
      <Box
        sx={{
          maxHeight: 300,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {filteredFiles.length === 0 ? (
          <Typography sx={{ p: 2 }} color="text.secondary">
            No files found
          </Typography>
        ) : (
          filteredFiles.map(file => <ConfirmFileRow key={file.id} file={file} />)
        )}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {filteredFiles.length} of {files.length} files
      </Typography>
    </Stack>
  );
}

ConfirmFileList.propTypes = {
  files: PropTypes.array.isRequired,
  description: PropTypes.string,
};

function ConfirmFileRow({ file }) {
  const pipelineName = file.pipeline?.name ?? 'Unknown Pipeline';
  const identitySyncs =
    file.identitySyncs?.map(sync => ({
      starterkitId: sync.identity_sync?.name,
      name: sync.identity_sync?.friendlyName,
    })) ?? [];

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body1" fontWeight={500} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {pipelineName}
        </Typography>
        {identitySyncs.length > 0 && <IntegrationAvatarGroup integrations={identitySyncs} max={5} size="small" />}
        {file.updatedAt && (
          <Stack spacing={0} sx={{ minWidth: 140, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              Last updated:
            </Typography>
            <TimeAgoAndDate
              timestamp={file.updatedAt}
              TimeAgoProps={{ TypographyProps: { variant: 'caption', color: 'text.secondary' } }}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

ConfirmFileRow.propTypes = {
  file: PropTypes.object.isRequired,
};
